import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";

interface GroupSalesItem {
  productId: string;
  productName: string;
  quantity: number;
  totalValue: number;
}

interface GroupSalesReport {
  items: GroupSalesItem[];
  totalQuantity: number;
  totalValue: number;
}

interface UseGroupSalesReportParams {
  categoryId: string | null;
  dateFrom: Date;
  dateTo: Date;
}

export function useGroupSalesReport({ categoryId, dateFrom, dateTo }: UseGroupSalesReportParams) {
  const { activeRestaurant } = useRestaurantContext();

  return useQuery({
    queryKey: ["group-sales-report", activeRestaurant?.id, categoryId, dateFrom.toISOString(), dateTo.toISOString()],
    queryFn: async (): Promise<GroupSalesReport> => {
      if (!activeRestaurant?.id || !categoryId) {
        return { items: [], totalQuantity: 0, totalValue: 0 };
      }

      // Get products in this category
      const { data: products, error: prodError } = await supabase
        .from("products")
        .select("id, name")
        .eq("restaurant_id", activeRestaurant.id)
        .eq("category_id", categoryId);

      if (prodError) throw prodError;
      if (!products || products.length === 0) {
        return { items: [], totalQuantity: 0, totalValue: 0 };
      }

      const productIds = products.map((p) => p.id);
      const productNameMap = new Map(products.map((p) => [p.id, p.name]));

      // Get orders in date range
      const fromStr = dateFrom.toISOString().split("T")[0];
      const toDate = new Date(dateTo);
      toDate.setDate(toDate.getDate() + 1);
      const toStr = toDate.toISOString().split("T")[0];

      const { data: orders, error: ordError } = await supabase
        .from("orders")
        .select("id")
        .eq("restaurant_id", activeRestaurant.id)
        .gte("created_at", fromStr)
        .lt("created_at", toStr)
        .neq("status", "cancelled");

      if (ordError) throw ordError;
      if (!orders || orders.length === 0) {
        return { items: [], totalQuantity: 0, totalValue: 0 };
      }

      const orderIds = orders.map((o) => o.id);

      // Fetch order items in batches to avoid URL length limits
      const batchSize = 50;
      let allItems: { product_id: string | null; quantity: number; total_price: number }[] = [];

      for (let i = 0; i < orderIds.length; i += batchSize) {
        const batch = orderIds.slice(i, i + batchSize);
        const { data: items, error: itemError } = await supabase
          .from("order_items")
          .select("product_id, quantity, total_price")
          .in("order_id", batch)
          .in("product_id", productIds);

        if (itemError) throw itemError;
        if (items) allItems = allItems.concat(items);
      }

      // Aggregate by product
      const aggregated = new Map<string, { quantity: number; totalValue: number }>();

      for (const item of allItems) {
        if (!item.product_id) continue;
        const existing = aggregated.get(item.product_id) || { quantity: 0, totalValue: 0 };
        existing.quantity += item.quantity;
        existing.totalValue += item.total_price;
        aggregated.set(item.product_id, existing);
      }

      const items: GroupSalesItem[] = Array.from(aggregated.entries())
        .map(([productId, data]) => ({
          productId,
          productName: productNameMap.get(productId) || "Produto removido",
          quantity: data.quantity,
          totalValue: data.totalValue,
        }))
        .sort((a, b) => b.quantity - a.quantity);

      const totalQuantity = items.reduce((sum, i) => sum + i.quantity, 0);
      const totalValue = items.reduce((sum, i) => sum + i.totalValue, 0);

      return { items, totalQuantity, totalValue };
    },
    enabled: !!activeRestaurant?.id && !!categoryId,
  });
}
